import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Row, Col, Card, Form, Button, Badge, Alert, InputGroup,
} from 'react-bootstrap';
import {
  FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle,
  FaLayerGroup, FaShoppingBag, FaTimes,
} from 'react-icons/fa';
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import CustomerActivities from '../../Components/CustomerActivities';
import BackButton from '../../Components/BackButton';
import { toast } from 'react-toastify';
import { useUpdateUserMutation, useGetUserDetailsQuery } from '../../slices/usersApiSlice';
import {
  useAdminAssignUserTierMutation,
  useAdminListPricingTiersQuery,
  useAdminRemoveUserTierMutation,
} from '../../slices/partnerCodesApiSlice';

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : '—';

const fmtCurrency = (amount) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(Number(amount || 0));

const UserEditScreen = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();

  /* ── form state ── */
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isAdmin,  setIsAdmin]  = useState(false);
  const [endOfMonthPaymentEnabled, setEndOfMonthPaymentEnabled] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [tierId,   setTierId]   = useState('');

  /* ── queries ── */
  const { data: user, isLoading, refetch, error } = useGetUserDetailsQuery(userId);
  const { data: tiers = [], isLoading: loadingTiers } = useAdminListPricingTiersQuery();

  /* ── mutations ── */
  const [updateUser,  { isLoading: loadingUpdate }]  = useUpdateUserMutation();
  const [assignTier,  { isLoading: loadingAssign }]  = useAdminAssignUserTierMutation();
  const [removeTier,  { isLoading: loadingRemove }]  = useAdminRemoveUserTierMutation();

  /* ── seed form from server ── */
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setIsAdmin(Boolean(user.isAdmin));
      setEndOfMonthPaymentEnabled(Boolean(user.endOfMonthPaymentEnabled));
      setFreeDelivery(Boolean(user.freeDelivery));
      setTierId(user?.tier?.id || '');
    }
  }, [user]);

  /* ── handlers ── */
  const submitHandler = async (e) => {
    e.preventDefault();
    const payload = { userId, name, email, isAdmin, endOfMonthPaymentEnabled, freeDelivery };
    if (password.trim().length > 0) {
      if (password.trim().length < 6) {
        toast.error('Password must be at least 6 characters.');
        return;
      }
      payload.password = password.trim();
    }
    try {
      await updateUser(payload).unwrap();
      toast.success('User updated successfully.');
      setPassword('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Update failed.');
    }
  };

  const assignTierHandler = async () => {
    if (!tierId) { toast.error('Select a tier to assign.'); return; }
    try {
      await assignTier({ userId, tierId }).unwrap();
      toast.success('Tier assigned.');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Tier assign failed.');
    }
  };

  const removeTierHandler = async () => {
    if (!window.confirm('Remove pricing tier from this user?')) return;
    try {
      await removeTier({ userId }).unwrap();
      toast.success('Tier removed.');
      setTierId('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Tier removal failed.');
    }
  };

  /* ── loading / error states ── */
  if (isLoading) return <Loader />;
  if (error)
    return (
      <>
        <BackButton />
        <Message variant="danger">{error?.data?.message || error?.error || 'Failed to load user.'}</Message>
      </>
    );

  const hasTier = Boolean(user?.tier?.id);
  const orderStats = user?.orderStats || {};
  const customerOrdersSearch = new URLSearchParams({
    userId: user?._id || '',
    userName: user?.name || '',
    userEmail: user?.email || '',
  }).toString();

  return (
    <>
      <BackButton />
      <h1 className="mb-4">Edit User</h1>

      {(loadingUpdate || loadingAssign || loadingRemove) && <Loader />}

      <Row className="g-4">
        {/* ── Left column: Account Details ── */}
        <Col md={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom fw-semibold py-3">
              Account Details
            </Card.Header>
            <Card.Body>
              <Form onSubmit={submitHandler}>
                <Form.Group controlId="editName" className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="editEmail" className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group controlId="editPassword" className="mb-3">
                  <Form.Label>
                    Force New Password{' '}
                    <span className="text-muted fw-normal" style={{ fontSize: 12 }}>
                      (leave blank to keep current)
                    </span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPass ? 'text' : 'password'}
                      placeholder="Min 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPass((v) => !v)}
                      tabIndex={-1}
                    >
                      {showPass ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                </Form.Group>

                <hr />

                <Form.Group controlId="editIsAdmin" className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Administrator"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Grants full access to the admin panel.
                  </Form.Text>
                </Form.Group>

                <Form.Group controlId="editEOM" className="mb-4">
                  <Form.Check
                    type="switch"
                    label="End of Month Payment"
                    checked={endOfMonthPaymentEnabled}
                    onChange={(e) => setEndOfMonthPaymentEnabled(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Allows this user to defer payment to the end of the month.
                  </Form.Text>
                </Form.Group>

                <Form.Group controlId="editFreeDelivery" className="mb-4">
                  <Form.Check
                    type="switch"
                    label="Free Delivery"
                    checked={freeDelivery}
                    onChange={(e) => setFreeDelivery(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Allows this user to receive delivery/shipping at no cost.
                  </Form.Text>
                </Form.Group>

                <Button type="submit" variant="primary" disabled={loadingUpdate}>
                  Save Changes
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* ── Right column: Info + Tier ── */}
        <Col md={5}>
          {/* Account Info */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-bottom fw-semibold py-3">
              Account Info
            </Card.Header>
            <Card.Body className="small">
              <Row className="mb-2">
                <Col xs={5} className="text-muted">User ID</Col>
                <Col xs={7} className="font-monospace" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {user?._id}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">Member Since</Col>
                <Col xs={7}>{fmtDate(user?.createdAt)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">Last Updated</Col>
                <Col xs={7}>{fmtDate(user?.updatedAt)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5} className="text-muted">Email Status</Col>
                <Col xs={7}>
                  {user?.emailVerified ? (
                    <Badge bg="success" className="d-inline-flex align-items-center gap-1">
                      <FaCheckCircle /> Verified
                    </Badge>
                  ) : (
                    <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1">
                      <FaTimesCircle /> Unverified
                    </Badge>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Customer Order Summary */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Header className="bg-white border-bottom fw-semibold py-3">
              Customer Order Summary
            </Card.Header>
            <Card.Body className="small">
              <Row className="mb-2">
                <Col xs={7} className="text-muted">Total Orders</Col>
                <Col xs={5} className="text-end fw-semibold">
                  {Number(orderStats.totalOrders || 0)}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={7} className="text-muted">Total Spent</Col>
                <Col xs={5} className="text-end fw-semibold">
                  {fmtCurrency(orderStats.totalSpent)}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={7} className="text-muted">Outstanding Balance</Col>
                <Col xs={5} className="text-end fw-semibold">
                  {fmtCurrency(orderStats.outstandingBalance)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={7} className="text-muted">Last Order</Col>
                <Col xs={5} className="text-end">
                  {fmtDate(orderStats.lastOrderDate)}
                </Col>
              </Row>
              <Link
                to={`/admin/orderlist?${customerOrdersSearch}`}
                className="btn btn-outline-secondary btn-sm w-100"
              >
                <FaShoppingBag className="me-1" /> View Customer Orders
              </Link>
            </Card.Body>
          </Card>

          {/* Pricing Tier */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom fw-semibold py-3 d-flex align-items-center gap-2">
              <FaLayerGroup className="text-success" /> Pricing Tier
            </Card.Header>
            <Card.Body>
              {/* Current tier */}
              {hasTier ? (
                <Alert variant="success" className="d-flex align-items-center justify-content-between py-2 mb-3">
                  <span>
                    <strong>Current:</strong> {user.tier.name}
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-danger"
                    onClick={removeTierHandler}
                    disabled={loadingRemove}
                    title="Remove tier"
                  >
                    <FaTimes />
                  </Button>
                </Alert>
              ) : (
                <Alert variant="secondary" className="py-2 mb-3" style={{ fontSize: 13 }}>
                  No tier assigned.
                </Alert>
              )}

              {/* Assign / change tier */}
              {loadingTiers ? (
                <Loader />
              ) : (
                <>
                  <Form.Group controlId="tierSelect" className="mb-2">
                    <Form.Label style={{ fontSize: 13 }}>
                      {hasTier ? 'Change Tier' : 'Assign Tier'}
                    </Form.Label>
                    <Form.Select
                      value={tierId}
                      onChange={(e) => setTierId(e.target.value)}
                      size="sm"
                    >
                      <option value="">— select tier —</option>
                      {tiers.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={assignTierHandler}
                    disabled={loadingAssign || !tierId}
                    className="w-100"
                  >
                    {loadingAssign ? 'Saving…' : hasTier ? 'Change Tier' : 'Assign Tier'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <CustomerActivities key={userId} customerId={userId} />
    </>
  );
};

export default UserEditScreen;
